import { useState, useEffect, useCallback, useRef } from 'react';
import { getApiBase } from './config/apiBase';
import { useOktaAuth } from '@okta/okta-react';
import { useNavigate } from 'react-router-dom';
import { models as fallbackModels } from './ModelSelector';
import DeployIntakeForm from './DeployIntakeForm';
import EmailApiModal from './components/EmailApiModal';
import AiApiModal from './components/AiApiModal';
import GoogleDocsApiModal from './components/GoogleDocsApiModal';
import UsersModal from './components/UsersModal';
import VersionLogModal from './components/VersionLogModal';
import RedeployVersionModal from './components/RedeployVersionModal';
import LyricConfirm from './components/LyricConfirm';
import FitText from './components/FitText';
import AppViewerOverlay from './components/AppViewerOverlay';
import AdminAppsPage from './admin/AdminAppsPage';
import AdminDeployActivity from './admin/AdminDeployActivity';
import AdminEstimationConfig from './admin/AdminEstimationConfig';
import AdminNetCheck from './admin/AdminNetCheck';
import AdminRepoHealth from './admin/AdminRepoHealth';
import AdminCredentials from './admin/AdminCredentials';
import AdminTesterLab from './admin/AdminTesterLab';
import AdminSalesforceSync from './admin/AdminSalesforceSync';
import AdminAwsLogsAccess from './admin/AdminAwsLogsAccess';
import AdminSalesforceApiAccess from './admin/AdminSalesforceApiAccess';
import AdminSms from './admin/AdminSms';
import AdminAccessControl from './admin/AdminAccessControl';
import { formatBuild as fmtBuild } from './utils/formatBuild';
import { VERSION_INFO } from './version';
import { viewAsHeaders } from './debug/viewAs';
import { authService } from './services/authService';
import LogoMain from './LogoMain.png';
import LyricLogo from './LyricLogo.png';

const API_URL = getApiBase();

// Admin tools shown in the sidebar "Admin" section. Mirrors
// components/AdminMenu.jsx (keep in sync). Each renders INLINE in the content
// pane (keyed by `key`) rather than navigating away, so the sidebar stays put.
// "App Manager" is handled separately (it opens the deploy modal).
// `toolId` marks a tool that a non-admin can be delegated access to (see
// server/services/toolAccessStore.js); `fullAdminOnly` marks a tool that only
// a full admin can see.
const ADMIN_TOOLS = [
  { key: 'admin/apps',        label: 'Deployed Apps',     icon: '📦', Component: AdminAppsPage },
  { key: 'admin/deploy-activity', label: 'Deploy Activity', icon: '📜', Component: AdminDeployActivity, fullAdminOnly: true },
  { key: 'admin/estimation',  label: 'Estimation',        icon: '📏', Component: AdminEstimationConfig },
  { key: 'admin/repo-health', label: 'Repo Health',       icon: '🩺', Component: AdminRepoHealth },
  { key: 'admin/credentials', label: 'API Keys',          icon: '🔑', Component: AdminCredentials },
  { key: 'admin/netcheck',    label: 'Network',           icon: '🌐', Component: AdminNetCheck },
  { key: 'admin/tester-lab',  label: 'Tester Lab',        icon: '🧪', Component: AdminTesterLab },
  { key: 'admin/sync',        label: 'JIRA → Salesforce', icon: '🔄', Component: AdminSalesforceSync },
  { key: 'admin/aws-logs',    label: 'AWS Logs Access',   icon: '☁️', Component: AdminAwsLogsAccess, fullAdminOnly: true },
  { key: 'admin/salesforce-api', label: 'Salesforce API', icon: '⚡', Component: AdminSalesforceApiAccess, toolId: 'salesforce-api' },
  { key: 'admin/sms',         label: 'SMS',               icon: '💬', Component: AdminSms, toolId: 'sms' },
  { key: 'admin/access-control', label: 'Access Control', icon: '🔐', Component: AdminAccessControl, fullAdminOnly: true },
];

/**
 * The dashboard launch screen (the only launch screen — it fully replaced the
 * classic LandingPage). Sidebar-style, loosely modeled on an admin console:
 *
 *   - Left sidebar (dark): RapidAI logo + compact AI-model picker on top, then
 *     "Main" (Applications, App Manager) and "Admin" (admin tools) sections,
 *     with the signed-in user and Sign Out at the bottom.
 *   - Top bar: page title + Auto-arrange (reset tile positions).
 *   - Content: free-canvas grid of uniform app cards (built-in + deployed);
 *     drag a tile's ⠿ handle to place it anywhere. Apps the user manages expose
 *     the full admin tool set via a ⋯ menu.
 *
 * Data comes from GET /api/deploy/list, GET /api/me/admin-status, /api/models.
 */

const NewDashboard = ({ onStart, selectedModel, setSelectedModel }) => {
  const navigate = useNavigate();
  const oktaAuthResult = useOktaAuth();
  const oktaAuth = oktaAuthResult?.oktaAuth;
  const isOktaAvailable = oktaAuth !== undefined;

  const [liveApps, setLiveApps] = useState({});
  const [isAdmin, setIsAdmin] = useState(false);
  const [toolAccess, setToolAccess] = useState([]);
  const [email, setEmail] = useState('');
  const [activeView, setActiveView] = useState('apps'); // 'apps' | admin tool key
  const [modelOptions, setModelOptions] = useState(fallbackModels || []);
  const [showDeployIntake, setShowDeployIntake] = useState(false); // false | true | {edit object}
  const [confirmLogout, setConfirmLogout] = useState(false);
  const [busyApps, setBusyApps] = useState({});            // start/stop in flight
  const [redeployingApps, setRedeployingApps] = useState({}); // redeploy in flight
  const [openMenu, setOpenMenu] = useState(null);          // appName whose menu is open
  const [hiddenAppNames, setHiddenAppNames] = useState([]); // personal "hide from my dashboard" (localStorage)
  const [showHidden, setShowHidden] = useState(false);     // reveal the collapsed Hidden section
  const [appPositions, setAppPositions] = useState({}); // free-canvas tile positions {name:{x,y}} (localStorage)
  const [draggingName, setDraggingName] = useState(null); // tile currently being dragged (for the lifted style)
  const [canvasW, setCanvasW] = useState(0);              // measured width of the apps canvas (for default layout)
  const menuRef = useRef(null);
  const dragRef = useRef(null);                            // live drag bookkeeping (name, pointer offset)
  const canvasRef = useRef(null);                          // the free-positioning apps canvas element

  // Per-app management modals (same components the classic screen uses).
  const [emailApiApp, setEmailApiApp] = useState(null);
  const [aiApiApp, setAiApiApp] = useState(null);
  const [docsApiApp, setDocsApiApp] = useState(null);
  const [coAdminsApp, setCoAdminsApp] = useState(null);
  const [versionLogApp, setVersionLogApp] = useState(null);
  const [redeployPromptApp, setRedeployPromptApp] = useState(null);
  // Deployed app being viewed in-window (contained iframe overlay) — the app
  // object { appName, displayName, url, status } or null.
  const [viewerApp, setViewerApp] = useState(null);

  const authFetch = useCallback(async (url, options = {}) => {
    let token = '';
    try { token = isOktaAvailable ? await oktaAuth.getAccessToken() : ''; } catch { /* dev mode */ }
    return fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: 'Bearer ' + token } : {}),
        ...viewAsHeaders(),
        ...options.headers,
      },
    });
  }, [oktaAuth, isOktaAvailable]);

  const fetchLiveApps = useCallback(async () => {
    try {
      const res = await authFetch(`${API_URL}/api/deploy/list`);
      if (!res.ok) return;
      const data = await res.json().catch(() => ({ apps: [] }));
      const map = {};
      for (const app of (data.apps || [])) map[app.appName] = { ...app };
      setLiveApps(map);
    } catch { /* keep last known state */ }
  }, [authFetch]);

  // Admin gate + signed-in email (same endpoint AdminMenu uses).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch(`${API_URL}/api/me/admin-status`);
        const body = await res.json().catch(() => ({}));
        if (!cancelled && res.ok) {
          setIsAdmin(!!body.isAdmin);
          setToolAccess(Array.isArray(body.toolAccess) ? body.toolAccess : []);
          setEmail(body.email || '');
        }
      } catch { /* leave defaults */ }
    })();
    return () => { cancelled = true; };
  }, [authFetch]);

  // Live model list for the compact picker (fall back to the bundled list).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/models');
        const data = await res.json().catch(() => ({}));
        if (!cancelled && data.models?.length) setModelOptions(data.models);
      } catch { /* fallback list already set */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Keep the compact picker coherent: if the active model isn't in the current
  // option list (e.g. a stale ID from localStorage, or before the live list
  // loads), fall back to the first available option so the <select> never
  // renders a blank/unknown selection. Mirrors the classic ModelSelector guard.
  useEffect(() => {
    if (!modelOptions.length) return;
    const ids = modelOptions.map(m => m.id);
    if (!selectedModel || !ids.includes(selectedModel)) {
      setSelectedModel(modelOptions[0].id);
    }
  }, [modelOptions, selectedModel, setSelectedModel]);

  // Fixed-cadence poll (independent of liveApps to avoid a refetch loop).
  useEffect(() => {
    fetchLiveApps();
    const interval = setInterval(fetchLiveApps, 10000);
    return () => clearInterval(interval);
  }, [fetchLiveApps]);

  // Close the per-card action menu on outside click.
  useEffect(() => {
    if (!openMenu) return undefined;
    const onDocClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpenMenu(null);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [openMenu]);

  // Personal "hidden apps" list (localStorage) — mirrors the pinned-apps pattern
  // on the classic launch screen. Lets a user declutter their own dashboard
  // without affecting anyone else or the app's server-side visibility.
  useEffect(() => {
    try { setHiddenAppNames(JSON.parse(localStorage.getItem('lyric-hidden-apps') || '[]')); }
    catch { setHiddenAppNames([]); }
    try { setAppPositions(JSON.parse(localStorage.getItem('lyric-app-positions') || '{}') || {}); }
    catch { setAppPositions({}); }
  }, []);

  const persistHidden = (names) => {
    try { localStorage.setItem('lyric-hidden-apps', JSON.stringify(names)); } catch { /* storage unavailable */ }
  };
  const hideApp = (appName) => {
    setOpenMenu(null);
    setHiddenAppNames(prev => {
      const next = prev.includes(appName) ? prev : [...prev, appName];
      persistHidden(next);
      return next;
    });
  };
  const unhideApp = (appName) => {
    setOpenMenu(null);
    setHiddenAppNames(prev => {
      const next = prev.filter(n => n !== appName);
      persistHidden(next);
      return next;
    });
  };

  const resetPositions = () => {
    setAppPositions({});
    try { localStorage.removeItem('lyric-app-positions'); } catch { /* ignore */ }
  };

  // Track the apps-canvas width so un-placed tiles get a sensible default grid
  // layout (and dragging can clamp to the visible area).
  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return undefined;
    const measure = () => setCanvasW(el.clientWidth);
    measure();
    let ro;
    if (typeof ResizeObserver !== 'undefined') { ro = new ResizeObserver(measure); ro.observe(el); }
    else window.addEventListener('resize', measure);
    return () => { if (ro) ro.disconnect(); else window.removeEventListener('resize', measure); };
  }, [activeView]);

  const setBusy = (appName, val) => setBusyApps(prev => {
    const n = { ...prev };
    if (val) n[appName] = true; else delete n[appName];
    return n;
  });

  const handleStart = async (appName) => {
    setOpenMenu(null);
    setBusy(appName, true);
    try {
      const res = await authFetch(`${API_URL}/api/deploy/${encodeURIComponent(appName)}/start`, { method: 'POST' });
      if (res.ok) {
        setLiveApps(prev => ({ ...prev, [appName]: { ...(prev[appName] || {}), status: 'deploying' } }));
        setTimeout(fetchLiveApps, 1500);
      } else {
        const data = await res.json().catch(() => ({}));
        // eslint-disable-next-line no-alert
        alert(`Start failed (${res.status}): ${data.error || 'unknown error'}`);
      }
    } catch { /* network */ }
    setBusy(appName, false);
  };

  const handleStop = async (appName) => {
    setOpenMenu(null);
    setBusy(appName, true);
    try {
      const res = await authFetch(`${API_URL}/api/deploy/${encodeURIComponent(appName)}/stop`, { method: 'POST' });
      if (res.ok) {
        setLiveApps(prev => ({ ...prev, [appName]: { ...(prev[appName] || {}), status: 'stopped' } }));
      } else {
        const data = await res.json().catch(() => ({}));
        // eslint-disable-next-line no-alert
        alert(`Stop failed (${res.status}): ${data.error || 'unknown error'}`);
      }
    } catch { /* network */ }
    setBusy(appName, false);
  };

  // In-place redeploy from the app's registry config (preserves sticky fields).
  const handleRedeployClick = async (appName, displayName, bumpType = 'minor') => {
    setOpenMenu(null);
    setRedeployingApps(prev => ({ ...prev, [appName]: true }));
    try {
      const res = await authFetch(`${API_URL}/api/deploy/${encodeURIComponent(appName)}/redeploy`, {
        method: 'POST',
        body: JSON.stringify({ bumpType }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        if (res.status === 409 && data.code === 'NO_CHANGES') {
          // eslint-disable-next-line no-alert
          window.alert(data.error || 'No code changes since the last deploy — redeploy skipped.');
        } else {
          // eslint-disable-next-line no-alert
          window.alert(`Redeploy failed (${res.status}): ${data.error || 'unknown error'}`);
        }
        return;
      }
      const POLL_TIMEOUT = 3 * 60 * 1000;
      const pollStart = Date.now();
      const poll = async () => {
        if (Date.now() - pollStart > POLL_TIMEOUT) return;
        const statusRes = await authFetch(`${API_URL}/api/deploy/status/${encodeURIComponent(appName)}`);
        const data = await statusRes.json().catch(() => ({}));
        await fetchLiveApps();
        if (['running', 'error', 'stopped'].includes(data.status)) return;
        await new Promise(r => setTimeout(r, 3000));
        return poll();
      };
      await poll();
    } catch (err) {
      console.error('[Redeploy]', err.message);
    } finally {
      setRedeployingApps(prev => { const n = { ...prev }; delete n[appName]; return n; });
    }
  };

  const openApp = (app) => {
    if (!app?.url) return;
    // Open contained in-window (iframe overlay) instead of a new browser tab.
    setViewerApp({
      appName: app.appName,
      displayName: app.displayName || app.appName,
      url: app.url,
      status: app.status,
    });
  };

  const openWithRapidChat = (appName) => {
    setOpenMenu(null);
    if (!appName) return;
    window.open(`/tester/live/${encodeURIComponent(appName)}`, '_blank', 'noopener,noreferrer');
  };

  // Open the deploy form pre-filled for editing this app (mirrors classic Edit).
  const editApp = (app) => {
    setOpenMenu(null);
    setShowDeployIntake({
      owner: app.owner,
      repo: app.repo,
      branch: app.branch || 'main',
      appName: app.appName,
      displayName: app.displayName || '',
      secretName: app.secretName || '',
      allowedGroups: Array.isArray(app.allowedGroups) ? app.allowedGroups : [],
      coAdmins: Array.isArray(app.coAdmins) ? app.coAdmins : [],
      forwardOktaToken: !!app.forwardOktaToken,
      ownerOnly: !!app.ownerOnly,
      subdir: app.subdir || '',
      startCommand: app.startCommand || '',
      buildCommand: app.buildCommand || '',
      version: app.version || '',
    });
  };

  const handleSignOut = async () => {
    setConfirmLogout(false);
    if (!isOktaAvailable) return;
    try {
      try { sessionStorage.setItem('lyric.justLoggedOut', '1'); } catch { /* ignore */ }
      await authService.destroySession();
      try { await oktaAuth.tokenManager.clear(); } catch { /* ignore */ }
      await oktaAuth.signOut();
    } catch (err) {
      console.error('Sign out error:', err);
    }
  };

  // --- App card model ---------------------------------------------------

  const statusMeta = (status) => {
    switch (status) {
      case 'running':   return { label: 'Live', dot: '#4caf50', border: '#2e7d32' };
      case 'idle':      return { label: 'Sleeping', dot: '#9575cd', border: '#5e35b1' };
      case 'error':     return { label: 'Error', dot: '#ef5350', border: '#c62828' };
      case 'deploying': return { label: 'Deploying', dot: '#4fc3f7', border: '#0277bd' };
      case 'ready':     return { label: 'Ready', dot: '#4caf50', border: '#2e7d32' };
      default:          return { label: 'Offline', dot: '#8a8a8a', border: '#3a3a3a' };
    }
  };

  const builtInCards = [
    {
      key: 'lyric-dev', appName: 'lyric-dev', builtIn: true,
      displayName: 'Lyric — Intelligent Development', subtitle: 'Built-in workspace',
      logo: LyricLogo, status: 'ready', cta: 'Launch', onOpen: onStart,
    },
    {
      key: 'ai-performance', appName: 'ai-performance', builtIn: true,
      displayName: 'AI Performance', subtitle: 'Built-in analytics',
      emoji: '📊', status: 'ready', cta: 'Open', onOpen: () => navigate('/perf-dashboard'),
    },
  ];

  const deployedCards = Object.entries(liveApps)
    .map(([appName, app]) => ({ appName, ...app }))
    .sort((a, b) => (a.displayName || a.appName).localeCompare(b.displayName || b.appName));

  const hiddenSet = new Set(hiddenAppNames);
  const visibleBuiltInCards = builtInCards.filter(c => !hiddenSet.has(c.appName));
  const visibleDeployedCards = deployedCards.filter(c => !hiddenSet.has(c.appName));
  const hiddenCards = [...builtInCards, ...deployedCards].filter(c => hiddenSet.has(c.appName));

  const orderableCards = [...visibleBuiltInCards, ...visibleDeployedCards];

  // Free-canvas layout. Un-placed tiles flow into a default grid (left→right,
  // top→bottom) based on the measured canvas width; once you drag a tile, its
  // explicit {x,y} in appPositions wins and it stays exactly where you drop it.
  const GAP = 18;
  const cols = Math.max(1, Math.floor((canvasW + GAP) / (CARD_W + GAP)));
  const defaultPos = (i) => ({ x: (i % cols) * (CARD_W + GAP), y: Math.floor(i / cols) * (CARD_H + GAP) });
  const posOf = (name, i) => appPositions[name] || defaultPos(i);
  const displayCards = orderableCards;
  // Canvas must be tall enough to hold every tile (plus room to drop lower).
  const canvasH = displayCards.reduce((max, c, i) => Math.max(max, posOf(c.appName, i).y + CARD_H), 0) + GAP;

  // Pointer-capture free drag. On pointerdown we capture the pointer on the ⠿
  // handle (proven pattern from LiveTesterOverlay — every move/up routes here no
  // matter what's under the cursor). The tile follows the cursor because its
  // absolute {x,y} updates live; on release we persist. No native drag-and-drop,
  // so a tile can never "snap back".
  const startDrag = (e, appName) => {
    if (e.button != null && e.button !== 0) return; // primary button only
    const handle = e.currentTarget;
    const cardEl = handle.closest ? handle.closest('[data-app]') : null;
    const rect = (cardEl || handle).getBoundingClientRect();
    try { handle.setPointerCapture(e.pointerId); } catch { /* older browsers */ }
    dragRef.current = {
      name: appName,
      pointerId: e.pointerId,
      dx: e.clientX - rect.left, // pointer offset within the tile
      dy: e.clientY - rect.top,
    };
    try { document.body.style.userSelect = 'none'; } catch { /* noop */ }
    setDraggingName(appName);
  };

  const onDragMove = (e) => {
    const d = dragRef.current;
    const canvas = canvasRef.current;
    if (!d || !canvas) return;
    e.preventDefault();
    const crect = canvas.getBoundingClientRect();
    let x = e.clientX - crect.left - d.dx;
    let y = e.clientY - crect.top - d.dy;
    x = Math.max(0, Math.min(x, Math.max(0, crect.width - CARD_W))); // keep inside the canvas width
    y = Math.max(0, y);
    setAppPositions(prev => ({ ...prev, [d.name]: { x, y } }));
  };

  const endDrag = (e) => {
    const d = dragRef.current;
    dragRef.current = null;
    try { document.body.style.userSelect = ''; } catch { /* noop */ }
    setDraggingName(null);
    if (!d) return;
    try { if (e.currentTarget.releasePointerCapture) e.currentTarget.releasePointerCapture(d.pointerId); } catch { /* noop */ }
    setAppPositions(prev => {
      try { localStorage.setItem('lyric-app-positions', JSON.stringify(prev)); } catch { /* noop */ }
      return prev;
    });
  };

  const renderMenu = (card) => {
    const { appName, displayName } = card;
    const isRunning = card.status === 'running';
    const isIdle = card.status === 'idle';
    const isError = card.status === 'error';
    const manager = !!(card.isManager ?? card.isOwner);
    const canRapidChat = isAdmin || manager || card.isTester;
    const name = displayName || appName;
    return (
      <div role="menu" style={styles.menu}>
        {canRapidChat && (isRunning || isIdle) && (
          <button type="button" style={styles.menuItem} onClick={() => openWithRapidChat(appName)}>
            🤖 Open in Tester
          </button>
        )}
        {manager && <div style={styles.menuDivider} />}
        {manager && (
          <button type="button" style={styles.menuItem} onClick={() => { setOpenMenu(null); setRedeployPromptApp({ appName, displayName: name, currentVersion: card.version || '' }); }}>
            {redeployingApps[appName] ? '⏳ Redeploying…' : '🔄 Redeploy'}
          </button>
        )}
        {manager && (isRunning || isIdle || isError) && (
          <button type="button" style={styles.menuItem} onClick={() => handleStop(appName)}>
            ⏹ Stop
          </button>
        )}
        {manager && (card.status === 'stopped' || card.status === 'offline' || !card.status) && (
          <button type="button" style={styles.menuItem} onClick={() => handleStart(appName)}>
            ▶️ Start
          </button>
        )}
        {manager && (
          <button type="button" style={styles.menuItem} onClick={() => editApp(card)}>
            ✏️ Edit
          </button>
        )}
        {manager && (
          <button type="button" style={styles.menuItem} onClick={() => { setOpenMenu(null); setEmailApiApp({ appName, displayName: name }); }}>
            ✉️ Email API
          </button>
        )}
        {manager && (
          <button type="button" style={styles.menuItem} onClick={() => { setOpenMenu(null); setAiApiApp({ appName, displayName: name }); }}>
            🤖 Ask Lyric API
          </button>
        )}
        {manager && (
          <button type="button" style={styles.menuItem} onClick={() => { setOpenMenu(null); setDocsApiApp({ appName, displayName: name }); }}>
            📄 Google Docs API
          </button>
        )}
        {manager && (
          <button type="button" style={styles.menuItem} onClick={() => { setOpenMenu(null); setActiveView('admin/salesforce-api'); }}>
            ⚡ Salesforce API
          </button>
        )}
        {manager && (
          <button type="button" style={styles.menuItem} onClick={() => { setOpenMenu(null); setCoAdminsApp({ appName, displayName: name }); }}>
            👥 Users & Visibility
          </button>
        )}
        {manager && (
          <button type="button" style={styles.menuItem} onClick={() => { setOpenMenu(null); setVersionLogApp({ appName, displayName: name }); }}>
            🕘 Version Log
          </button>
        )}
        {((canRapidChat && (isRunning || isIdle)) || manager) && <div style={styles.menuDivider} />}
        {hiddenSet.has(appName) ? (
          <button type="button" style={styles.menuItem} onClick={() => unhideApp(appName)}>
            🙈 Unhide
          </button>
        ) : (
          <button type="button" style={styles.menuItem} onClick={() => hideApp(appName)}>
            🙈 Hide from my dashboard
          </button>
        )}
      </div>
    );
  };

  const renderCard = (card, draggable = false, pos = null) => {
    const { appName, displayName, subtitle, builtIn, status, cta, onOpen, logo, emoji, deployedBy, version, buildNumber, ownerOnly } = card;
    const meta = statusMeta(status);
    // Hidden/private apps (owner-only visibility) get an orange ring so the
    // owner can spot them at a glance. `ownerOnly` is only present on apps the
    // current user manages (see GET /api/deploy/list), so this never leaks.
    const isHidden = !builtIn && !!ownerOnly;
    const isRunning = status === 'running';
    const isIdle = status === 'idle';
    const isDeploying = status === 'deploying';
    const isError = status === 'error';
    const manager = !builtIn && !!(card.isManager ?? card.isOwner);
    const hasMenu = true; // every app (including built-in) exposes the Hide/Unhide action
    const busy = !!busyApps[appName] || !!redeployingApps[appName] || isDeploying;
    const isDragging = draggingName === appName;

    let primaryLabel = cta || 'Open';
    let primaryAction = () => (builtIn ? onOpen && onOpen() : openApp(card));
    let primaryDisabled = false;
    if (!builtIn) {
      if (isRunning) { primaryLabel = 'Open'; }
      else if (isIdle) { primaryLabel = 'Wake'; }
      else if (isDeploying) { primaryLabel = 'Deploying…'; primaryDisabled = true; }
      else if (isError) {
        if (manager) { primaryLabel = 'Restart'; primaryAction = () => handleStart(appName); }
        else { primaryLabel = 'Error'; primaryDisabled = true; }
      } else {
        if (manager) { primaryLabel = 'Start'; primaryAction = () => handleStart(appName); }
        else { primaryLabel = 'Offline'; primaryDisabled = true; }
      }
      if (busy && !isError && !isDeploying) { primaryLabel = 'Working…'; primaryDisabled = true; }
    }

    return (
      <div
        key={card.key || appName}
        data-app={draggable ? appName : undefined}
        style={{
          ...styles.card,
          ...(draggable && pos ? { position: 'absolute', left: pos.x, top: pos.y, margin: 0 } : {}),
          borderColor: isHidden ? '#ff9800' : meta.border,
          ...(isHidden ? styles.cardHidden : {}),
          ...(isDragging ? styles.cardDragging : {}),
        }}
        title={isHidden ? 'Private — only you, your co-admins, and named testers can see this app' : undefined}
      >
        {draggable && (
          <div
            style={styles.dragHandle}
            onPointerDown={(e) => startDrag(e, appName)}
            onPointerMove={onDragMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            title="Drag to move this tile anywhere"
            aria-label="Drag to move this tile anywhere"
          >
            ⠿
          </div>
        )}
        {hasMenu && (
          <div style={styles.menuAnchor} ref={openMenu === appName ? menuRef : null}>
            <button
              type="button"
              onClick={() => setOpenMenu(openMenu === appName ? null : appName)}
              style={{ ...styles.menuButton, ...(openMenu === appName ? styles.menuButtonOpen : {}) }}
              title="App tools"
              aria-haspopup="menu"
              aria-expanded={openMenu === appName}
            >
              ⋯
            </button>
            {openMenu === appName && renderMenu(card)}
          </div>
        )}

        <div style={styles.cardIconWrap}>
          {logo ? <img src={logo} alt="" draggable={false} style={styles.cardLogo} />
            : emoji ? <span style={styles.cardEmoji}>{emoji}</span>
            : <img src={LogoMain} alt="" draggable={false} style={{ ...styles.cardLogo, opacity: 0.85 }} />}
        </div>

        <FitText text={displayName || appName} maxFontSize={15} minFontSize={11} style={styles.cardName} />

        <div style={styles.cardSubtitle}>
          {builtIn ? subtitle : (deployedBy ? `by ${deployedBy}` : 'Deployed app')}
        </div>

        <div style={styles.statusRow}>
          <span style={{ ...styles.statusDot, backgroundColor: meta.dot }} />
          <span style={styles.statusLabel}>{meta.label}</span>
          {!builtIn && version && (
            <span style={styles.versionBadge} title={`v${version}${fmtBuild(buildNumber) ? ` · build ${fmtBuild(buildNumber)}` : ''}`}>
              v{version}
            </span>
          )}
          {isHidden && (
            <span style={styles.privateBadge} title="Private — only you, your co-admins, and named testers can see this app">
              🔒 Private
            </span>
          )}
        </div>

        <button
          type="button"
          onClick={primaryAction}
          disabled={primaryDisabled}
          style={{
            ...styles.cardCta,
            ...(primaryDisabled ? styles.cardCtaDisabled : {}),
            ...(builtIn && card.key === 'lyric-dev' ? { backgroundColor: '#28a745', borderColor: '#28a745' } : {}),
          }}
        >
          {primaryLabel}
        </button>
      </div>
    );
  };

  const initials = (email || 'U').replace(/@.*/, '').slice(0, 2).toUpperCase();
  const totalApps = visibleBuiltInCards.length + visibleDeployedCards.length;
  const activeTool = activeView === 'apps' ? null : ADMIN_TOOLS.find(t => t.key === activeView);

  // Make Lyric the page's identity (browser tab) while the dashboard is mounted.
  useEffect(() => {
    const prev = document.title;
    document.title = activeView === 'apps'
      ? 'Lyric — Intelligent Development'
      : `Lyric — ${activeTool ? activeTool.label : 'Dashboard'}`;
    return () => { document.title = prev; };
  }, [activeView, activeTool]);
  // Full admins see every tool; a delegated (non-admin) user sees ONLY the
  // specific tool(s) they've been granted (toolAccessStore.js), never
  // "fullAdminOnly" ones like Access Control.
  const visibleAdminTools = ADMIN_TOOLS.filter((tool) => {
    if (isAdmin) return true;
    if (tool.fullAdminOnly || !tool.toolId) return false;
    return toolAccess.includes(tool.toolId);
  });

  return (
    <div style={styles.page}>
      {/* ===== Sidebar ===== */}
      <aside style={styles.sidebar}>
        <div style={styles.brand}>
          <div style={styles.brandLyricRow}>
            <img src={LyricLogo} alt="Lyric" style={styles.brandLyricLogo} />
            <span style={styles.brandLyricName}>Lyric</span>
          </div>
          <span style={styles.brandLyricTag}>Intelligent Development</span>
          <div style={styles.brandBy}>
            <span style={styles.brandByLabel}>Powered by</span>
            <img src={LogoMain} alt="RapidAI" style={styles.brandByLogo} />
          </div>
        </div>

        <div style={styles.modelWrap}>
          <label htmlFor="dash-model" style={styles.modelLabel}>AI Model</label>
          <select
            id="dash-model"
            value={selectedModel || ''}
            onChange={(e) => setSelectedModel(e.target.value)}
            style={styles.modelSelect}
          >
            {modelOptions.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        </div>

        <div style={styles.navSection}>
          <div style={styles.navHeader}>Main</div>
          <button type="button"
            style={activeView === 'apps' ? { ...styles.navItem, ...styles.navItemActive } : styles.navItem}
            onClick={() => setActiveView('apps')}
            onMouseEnter={(e) => { if (activeView !== 'apps') e.currentTarget.style.backgroundColor = '#2b3444'; }}
            onMouseLeave={(e) => { if (activeView !== 'apps') e.currentTarget.style.backgroundColor = 'transparent'; }}>
            <span style={styles.navIcon}>🏠</span> Applications
          </button>
          <button type="button" style={styles.navItem} onClick={() => setShowDeployIntake(true)}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#2b3444'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}>
            <span style={styles.navIcon}>📦</span> App Manager
          </button>
        </div>

        {(isAdmin || visibleAdminTools.length > 0) && (
          <div style={styles.navSection}>
            <div style={styles.navHeader}>Admin</div>
            {visibleAdminTools.map(tool => {
              const active = activeView === tool.key;
              return (
                <button key={tool.key} type="button"
                  style={active ? { ...styles.navItem, ...styles.navItemActive } : styles.navItem}
                  onClick={() => setActiveView(tool.key)}
                  onMouseEnter={(e) => { if (!active) e.currentTarget.style.backgroundColor = '#2b3444'; }}
                  onMouseLeave={(e) => { if (!active) e.currentTarget.style.backgroundColor = 'transparent'; }}>
                  <span style={styles.navIcon}>{tool.icon}</span> {tool.label}
                </button>
              );
            })}
          </div>
        )}

        <div style={styles.sidebarFooter}>
          <div style={styles.userRow}>
            <div style={styles.avatar}>{initials}</div>
            <div style={styles.userMeta}>
              <div style={styles.userEmail} title={email}>{email || 'Signed in'}</div>
              {isAdmin && <div style={styles.userRole}>Admin</div>}
            </div>
          </div>
          {isOktaAvailable && (
            <button type="button" onClick={() => setConfirmLogout(true)} style={styles.signOutButton}>
              Sign Out
            </button>
          )}
        </div>
      </aside>

      {/* ===== Content ===== */}
      <div style={styles.content}>
        <div style={styles.topbar}>
          <div>
            <h1 style={styles.pageTitle}>{activeTool ? activeTool.label : 'Applications'}</h1>
            {activeView === 'apps'
              ? <div style={styles.pageSub}>{totalApps} app{totalApps === 1 ? '' : 's'} available</div>
              : <div style={styles.pageSub}>Admin tool · sidebar stays put</div>}
          </div>
          <div style={styles.topbarActions}>
            {activeView === 'apps' && Object.keys(appPositions).length > 0 && (
              <button type="button" onClick={resetPositions} style={styles.classicButton} title="Auto-arrange: snap all tiles back into the tidy default grid">
                ⤢ Auto-arrange
              </button>
            )}
          </div>
        </div>

        {activeView === 'apps' ? (
          <main style={styles.main}>
            <div ref={canvasRef} style={{ ...styles.canvas, height: canvasH }}>
              {displayCards.map((card, i) => renderCard(card, true, posOf(card.appName, i)))}
            </div>
            {deployedCards.length === 0 && (
              <div style={styles.emptyHint}>
                No deployed apps yet. Use <strong>App Manager</strong> in the sidebar to deploy one.
              </div>
            )}

            {hiddenCards.length > 0 && (
              <div style={styles.hiddenSection}>
                <button
                  type="button"
                  onClick={() => setShowHidden(v => !v)}
                  style={styles.hiddenToggle}
                  aria-expanded={showHidden}
                  title="Apps you've hidden from your dashboard. Open an app's ⋯ menu and choose Unhide to bring it back."
                >
                  {showHidden ? '▾' : '▸'} 🙈 Hidden ({hiddenCards.length})
                </button>
                {showHidden && (
                  <div style={styles.grid}>
                    {hiddenCards.map(card => renderCard(card, false))}
                  </div>
                )}
              </div>
            )}
          </main>
        ) : (
          // Admin tool rendered INLINE in the content pane. The sidebar (sticky)
          // stays visible; the tool's own layout fills this scrollable region.
          // `embedded` lets a tool (currently only AdminSalesforceApiAccess)
          // skip its own <AdminMenu /> here — this sidebar already IS the
          // admin nav, and AdminMenu's links do a full-route navigate() that
          // would exit this inline pane. Unused/no-op for the other tools.
          <div style={styles.toolPane}>
            {activeTool && <activeTool.Component embedded />}
          </div>
        )}

        <footer style={styles.footer}>
          <div style={styles.footerBrand}>
            <span style={styles.footerLyric}>Lyric</span>
            <span style={styles.footerSep}>·</span>
            <span style={styles.footerMuted}>Powered by RapidAI</span>
          </div>
          <div style={styles.footerLegal}>
            © {new Date().getFullYear()} iSchemaView, Inc. (RapidAI). All rights reserved.
          </div>
          <div style={styles.footerRight}>
            <a href="https://www.rapidai.com" target="_blank" rel="noopener noreferrer" style={styles.footerLink}>RapidAI.com</a>
            <span style={styles.footerSep}>·</span>
            <a href="https://www.rapidai.com/privacy-policy" target="_blank" rel="noopener noreferrer" style={styles.footerLink}>Privacy</a>
            <span style={styles.footerSep}>·</span>
            <a href="/sms-terms" target="_blank" rel="noopener noreferrer" style={styles.footerLink}>SMS Terms</a>
            <span style={styles.footerSep}>·</span>
            <span
              style={styles.footerVersion}
              title={`Build ${VERSION_INFO.build} · commit ${VERSION_INFO.lastCommit} · ${new Date(VERSION_INFO.buildDate).toLocaleDateString()}`}
            >
              v{VERSION_INFO.version}
            </span>
          </div>
        </footer>
      </div>

      {/* ===== Modals ===== */}
      {viewerApp && (
        <AppViewerOverlay app={viewerApp} onClose={() => setViewerApp(null)} />
      )}
      {showDeployIntake && (
        <DeployIntakeForm
          onClose={() => { setShowDeployIntake(false); fetchLiveApps(); }}
          selectedModel={selectedModel}
          initialEdit={typeof showDeployIntake === 'object' ? showDeployIntake : undefined}
        />
      )}
      {emailApiApp && (
        <EmailApiModal appName={emailApiApp.appName} displayName={emailApiApp.displayName} apiUrl={API_URL} authFetch={authFetch} onClose={() => setEmailApiApp(null)} />
      )}
      {aiApiApp && (
        <AiApiModal appName={aiApiApp.appName} displayName={aiApiApp.displayName} apiUrl={API_URL} authFetch={authFetch} onClose={() => setAiApiApp(null)} />
      )}
      {docsApiApp && (
        <GoogleDocsApiModal appName={docsApiApp.appName} displayName={docsApiApp.displayName} apiUrl={API_URL} authFetch={authFetch} onClose={() => setDocsApiApp(null)} />
      )}
      {coAdminsApp && (
        <UsersModal appName={coAdminsApp.appName} displayName={coAdminsApp.displayName} apiUrl={API_URL} authFetch={authFetch} onClose={() => setCoAdminsApp(null)} />
      )}
      {versionLogApp && (
        <VersionLogModal appName={versionLogApp.appName} displayName={versionLogApp.displayName} apiUrl={API_URL} authFetch={authFetch} onClose={() => setVersionLogApp(null)} />
      )}
      {redeployPromptApp && (
        <RedeployVersionModal
          appName={redeployPromptApp.appName}
          displayName={redeployPromptApp.displayName}
          currentVersion={redeployPromptApp.currentVersion}
          apiUrl={API_URL}
          authFetch={authFetch}
          onCancel={() => setRedeployPromptApp(null)}
          onConfirm={(bumpType) => {
            const { appName, displayName } = redeployPromptApp;
            setRedeployPromptApp(null);
            handleRedeployClick(appName, displayName, bumpType);
          }}
        />
      )}
      <LyricConfirm
        isOpen={confirmLogout}
        tone="danger"
        title="Sign out of Lyric?"
        message="You'll be returned to the Okta sign-in screen."
        confirmLabel="Yes, sign out"
        cancelLabel="Cancel"
        onConfirm={handleSignOut}
        onCancel={() => setConfirmLogout(false)}
      />
    </div>
  );
};

const SIDEBAR_W = 248;
const CARD_W = 200;
const CARD_H = 220;

const styles = {
  page: {
    display: 'flex',
    minHeight: '100vh',
    backgroundColor: '#0f1115',
    color: '#e5e7eb',
    fontFamily: 'ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
  },

  // --- Sidebar ---
  sidebar: {
    width: SIDEBAR_W,
    flexShrink: 0,
    backgroundColor: '#232b36',
    borderRight: '1px solid #1a212b',
    display: 'flex',
    flexDirection: 'column',
    position: 'sticky',
    top: 0,
    height: '100vh',
    overflowY: 'auto',
    boxSizing: 'border-box',
  },
  brand: {
    padding: '22px 20px 18px',
    borderBottom: '1px solid #1a212b',
    background: 'linear-gradient(160deg, #273243 0%, #232b36 70%)',
  },
  brandLyricRow: { display: 'flex', alignItems: 'center', gap: 13 },
  brandLyricLogo: {
    width: 48, height: 'auto', flexShrink: 0,
    filter: 'drop-shadow(0 2px 6px rgba(79,140,255,0.35))',
  },
  brandLyricName: { fontSize: 32, fontWeight: 800, color: '#ffffff', letterSpacing: '-0.02em', lineHeight: 1 },
  brandLyricTag: {
    display: 'block', fontSize: 10, color: '#8ea2c0', fontWeight: 700, letterSpacing: '0.07em',
    textTransform: 'uppercase', marginTop: 10,
  },
  brandBy: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 16 },
  brandByLabel: {
    fontSize: 9, color: '#68758a', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
  },
  brandByLogo: { width: 72, height: 'auto', opacity: 0.75 },
  modelWrap: { display: 'flex', flexDirection: 'column', gap: 4, padding: '4px 20px 16px' },
  modelLabel: { fontSize: 10, color: '#7c8896', letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 700 },
  modelSelect: {
    width: '100%', padding: '7px 8px', fontSize: 13, backgroundColor: '#1b222c',
    color: '#e5e7eb', border: '1px solid #313b48', borderRadius: 6, cursor: 'pointer',
  },
  navSection: { padding: '8px 12px' },
  navHeader: {
    fontSize: 10, color: '#68758a', textTransform: 'uppercase', letterSpacing: '0.1em',
    fontWeight: 700, padding: '8px 10px 6px',
  },
  navItem: {
    display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left',
    padding: '10px 12px', fontSize: 14, fontWeight: 600, color: '#c3ccd9',
    backgroundColor: 'transparent', border: 'none', borderRadius: 8, cursor: 'pointer',
    transition: 'background-color 120ms ease',
  },
  navItemActive: {
    backgroundColor: '#0f1115', color: '#ffffff', borderLeft: '3px solid #4f8cff', paddingLeft: 9,
  },
  navIcon: { fontSize: 15, width: 18, textAlign: 'center' },
  sidebarFooter: { marginTop: 'auto', padding: '14px 16px', borderTop: '1px solid #1a212b' },
  userRow: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 },
  avatar: {
    width: 34, height: 34, borderRadius: '50%', flexShrink: 0, backgroundColor: '#3b4a63',
    color: '#dbe6ff', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 13, fontWeight: 700,
  },
  userMeta: { minWidth: 0 },
  userEmail: { fontSize: 12, color: '#dbe2ec', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 150 },
  userRole: { fontSize: 10, color: '#7c8896', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 },
  signOutButton: {
    width: '100%', padding: '8px 0', fontSize: 13, fontWeight: 600, color: '#fff',
    backgroundColor: '#c0392b', border: '1px solid #c0392b', borderRadius: 6, cursor: 'pointer',
  },

  // --- Content ---
  content: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' },
  topbar: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '20px 28px', borderBottom: '1px solid #1e2530', backgroundColor: '#14171d',
  },
  pageTitle: { margin: 0, fontSize: 22, fontWeight: 700, color: '#ffffff' },
  pageSub: { fontSize: 12, color: '#7c8896', marginTop: 3 },
  topbarActions: { display: 'flex', alignItems: 'center', gap: 10 },
  classicButton: {
    padding: '8px 14px', fontSize: 13, fontWeight: 600, color: '#c7cfdb',
    backgroundColor: '#1f2530', border: '1px solid #333b49', borderRadius: 6, cursor: 'pointer', whiteSpace: 'nowrap',
  },
  main: { padding: '28px', flex: 1 },
  // Inline admin-tool region. Fills the content pane below the top bar; the
  // embedded admin component supplies its own padding/background.
  toolPane: { flex: 1, minWidth: 0, overflowX: 'auto' },
  footer: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap',
    gap: 12, padding: '14px 28px', borderTop: '1px solid #1e2530', backgroundColor: '#12151b',
    fontSize: 12, color: '#68758a',
  },
  footerBrand: { display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 },
  footerLyric: { fontWeight: 800, color: '#cdd7e6', letterSpacing: '0.02em' },
  footerMuted: { color: '#68758a' },
  footerSep: { color: '#3a434f' },
  footerLegal: { flex: '1 1 auto', textAlign: 'center', color: '#7c8896', whiteSpace: 'nowrap' },
  footerRight: { display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 },
  footerLink: { color: '#8b97a8', textDecoration: 'none' },
  footerVersion: { color: '#5c6878', fontFamily: 'monospace', fontSize: 11, cursor: 'default' },
  grid: {
    display: 'grid',
    gridTemplateColumns: `repeat(auto-fill, ${CARD_W}px)`,
    justifyContent: 'start',
    gap: 18,
  },
  canvas: { position: 'relative', width: '100%', minHeight: CARD_H },
  card: {
    position: 'relative', width: CARD_W, height: CARD_H, display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'flex-start', backgroundColor: '#181c24',
    border: '1px solid #2a2f3a', borderRadius: 12, padding: '20px 16px 16px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.3)', boxSizing: 'border-box',
  },
  cardHidden: { boxShadow: '0 0 0 2px rgba(255,152,0,0.45), 0 2px 10px rgba(0,0,0,0.3)' },
  dragHandle: {
    position: 'absolute', top: 8, left: 8, width: 22, height: 22, lineHeight: '20px',
    textAlign: 'center', fontSize: 14, color: '#6b7684', cursor: 'grab', borderRadius: 6,
    userSelect: 'none', touchAction: 'none',
  },
  cardDragging: { zIndex: 1000, transform: 'scale(1.03)', boxShadow: '0 16px 36px rgba(0,0,0,0.6)', borderColor: '#4fc3f7', cursor: 'grabbing' },
  menuAnchor: { position: 'absolute', top: 8, right: 8 },
  menuButton: {
    width: 28, height: 28, lineHeight: '24px', fontSize: 18, color: '#9aa4b2',
    backgroundColor: 'transparent', border: '1px solid transparent', borderRadius: 6, cursor: 'pointer', padding: 0,
  },
  menuButtonOpen: { backgroundColor: '#20252f', borderColor: '#2c3340', color: '#e5e7eb' },
  menu: {
    position: 'absolute', top: 30, right: 0, minWidth: 190, maxHeight: 320, overflowY: 'auto',
    backgroundColor: '#1c2029', border: '1px solid #2e3340', borderRadius: 8,
    boxShadow: '0 12px 28px rgba(0,0,0,0.5)', padding: 4, zIndex: 50, textAlign: 'left',
  },
  menuItem: {
    display: 'block', width: '100%', textAlign: 'left', padding: '8px 10px', fontSize: 13,
    color: '#e5e7eb', backgroundColor: 'transparent', border: 'none', borderRadius: 6, cursor: 'pointer',
  },
  menuDivider: { height: 1, backgroundColor: '#2a2f3a', margin: '4px 6px' },
  cardIconWrap: { height: 52, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 10 },
  cardLogo: { maxWidth: 48, maxHeight: 48, objectFit: 'contain' },
  cardEmoji: { fontSize: 40, lineHeight: 1 },
  cardName: { fontWeight: 700, color: '#f3f4f6', textAlign: 'center', width: '100%', marginBottom: 2 },
  cardSubtitle: {
    fontSize: 11, color: '#8b93a1', textAlign: 'center', marginBottom: 8, height: 15,
    overflow: 'hidden', width: '100%', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
  },
  statusRow: { display: 'flex', alignItems: 'center', gap: 6, marginBottom: 'auto', flexWrap: 'wrap', justifyContent: 'center' },
  statusDot: { width: 8, height: 8, borderRadius: '50%', display: 'inline-block' },
  statusLabel: { fontSize: 12, color: '#c7cfdb', fontWeight: 600 },
  versionBadge: { fontSize: 10, color: '#9aa4b2', backgroundColor: '#20252f', border: '1px solid #2c3340', borderRadius: 4, padding: '1px 5px' },
  privateBadge: { fontSize: 10, color: '#ffb74d', backgroundColor: 'rgba(255,152,0,0.12)', border: '1px solid rgba(255,152,0,0.5)', borderRadius: 4, padding: '1px 5px' },
  cardCta: {
    marginTop: 12, width: '100%', padding: '9px 0', fontSize: 13, fontWeight: 700, color: '#fff',
    backgroundColor: '#1976d2', border: '1px solid #1976d2', borderRadius: 8, cursor: 'pointer',
  },
  cardCtaDisabled: { backgroundColor: '#2a2f3a', borderColor: '#2a2f3a', color: '#7b8494', cursor: 'not-allowed' },
  hiddenSection: { marginTop: 28, width: '100%' },
  hiddenToggle: {
    fontSize: 13, fontWeight: 700, color: '#c7cfdb', backgroundColor: 'transparent',
    border: '1px solid #2c3340', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', marginBottom: 14,
  },
  emptyHint: {
    marginTop: 20, padding: '18px', fontSize: 14, color: '#9aa4b2', backgroundColor: '#161a21',
    border: '1px dashed #2c3340', borderRadius: 10, textAlign: 'left', maxWidth: 520,
  },
};

export default NewDashboard;
